<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="6" rounded="xxl">
        <v-card-title class="mt-4">
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark> mdi-arrow-left </v-icon>
          </v-btn>
          Edit Room Background
        </v-card-title>

        <v-card-text>
          <v-row>
            <v-col sm="12">
              <!-- Form Fields -->
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="backgroundName"
                    label="Background Name"
                    placeholder="Enter name for this background"
                    dense
                    outlined
                    prepend-inner-icon="mdi-text"
                    :rules="[rules.required]"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6" v-if="selectedRoomId">
                  <v-select
                    v-model="selectedRoomId"
                    :items="rooms"
                    item-text="name"
                    item-value="id"
                    label="Select Room"
                    placeholder="Choose a room"
                    dense
                    outlined
                    disabled
                    prepend-inner-icon="mdi-home"
                    :loading="loadingRooms"
                    no-data-text="No rooms found"
                    @change="onRoomChange"
                  ></v-select>
                </v-col>
              </v-row>

              <!-- Single Image Upload Section -->
              <v-row justify="center">
                <v-col cols="12" sm="8" md="6">
                  <v-card elevation="2" class="image-slot-card">
                    <div class="image-slot-container">
                      <!-- File Input (Hidden) -->
                      <input
                        type="file"
                        id="background-file"
                        ref="backgroundFile"
                        accept="image/*"
                        @change="onFileChange"
                        style="display: none"
                      />

                      <!-- Image Preview or Upload Area -->
                      <div
                        v-if="!imagePreview"
                        class="upload-area"
                        @click="triggerFileInput"
                      >
                        <v-icon size="48" color="grey lighten-1">mdi-camera-plus</v-icon>
                        <p class="mt-2 text-center">Click to upload background image</p>
                        <small class="text-muted">Select new image file</small>
                      </div>

                      <div v-if="imagePreview" class="image-preview-container">
                        <img
                          :src="imagePreview"
                          alt="Background preview"
                          class="preview-image"
                          @error="onImageError"
                        />

                        <!-- Image Overlay Actions -->
                        <div class="image-overlay">
                          <v-btn
                            fab
                            x-small
                            color="blue"
                            dark
                            class="action-btn"
                            @click="viewImage(imagePreview)"
                          >
                            <v-icon color="white" small>mdi-eye</v-icon>
                          </v-btn>

                          <v-btn
                            fab
                            x-small
                            color="orange"
                            dark
                            class="action-btn"
                            @click="changeImage"
                          >
                            <v-icon color="white" small>mdi-camera</v-icon>
                          </v-btn>
                        </div>

                        <!-- Image Info -->
                        <div class="image-info">
                          <div class="image-name">{{ fileName }}</div>
                          <div class="image-size" v-if="fileSize > 0">{{ formatFileSize(fileSize) }}</div>
                        </div>
                      </div>
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </v-col>
          </v-row>

          <v-divider class="my-5"></v-divider>

          <!-- Action Buttons -->
          <div class="action-buttons">
            <v-btn
              rounded
              color="primary"
              :loading="isLoading"
              :disabled="isLoading || !hasChanges"
              @click="save"
              class="mr-3"
            >
              <v-icon left>mdi-content-save</v-icon>
              Update Background
            </v-btn>

            <v-btn
              rounded
              outlined
              color="grey"
              @click="$router.back()"
              :disabled="isLoading"
            >
              <v-icon left>mdi-cancel</v-icon>
              Cancel
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-container>

    <!-- Image View Dialog -->
    <v-dialog v-model="imageDialog" max-width="800">
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span>Image Preview</span>
          <v-btn icon @click="imageDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-0">
          <img :src="selectedImageUrl" style="width: 100%; height: auto" />
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-main>
</template>

<script>
import axios from "axios";

export default {
  name: "BannersCreatePage",
  data() {
    return {
      form: {},
      isLoading: false,
      loadingRooms: false,
      backgroundName: "",
      selectedRoomId: null,
      rooms: [],
      imagePreview: null,
      selectedFile: null,
      fileName: "",
      fileSize: 0,
      rules: {
        required: (value) => !!value || "This field is required",
      },
      imageDialog: false,
      selectedImageUrl: "",
      roomBackground: null,
      old_json: null,
      existingId: null,
    };
  },
  computed: {
    hasChanges() {
      return (
        this.selectedFile !== null || 
        (this.backgroundName && this.backgroundName.trim() !== "")
      );
    },
  },
  async mounted() {
    await Promise.all([
      this.getData(),
      this.getRooms()
    ]);
  },
  methods: {
    constructImageUrl(path) {
      if (!path) return null;
      if (path.startsWith('http://') || path.startsWith('https://')) {
        return path;
      }
      // Use media URL from store settings for images
      const mediaUrl = this.$store.getters['settings/getMediaUrl'] || 'https://blr1.vultrobjects.com/space-1/';
      const cleanPath = path.replace(/^\/+/, ''); // Remove all leading slashes
      return `${mediaUrl}${cleanPath}`;
    },
    async getData() {
      this.isLoading = true;
      let encryptedId = this.$route.params.backgroundId;
      
      try {
        const response = await axios.get(`/room_backgrounds/${encryptedId}`);
        
        if (response.data && response.data.data) {
          this.roomBackground = response.data.data;
          this.form = response.data.data;
          console.log('Room Background data:', this.form);
          this.old_json = response.data.data;
          
          // Handle single room background object from API
          const bg = this.roomBackground;
          if (bg) {
            this.backgroundName = bg.name || '';
            this.selectedRoomId = bg.room_id || null;

            if (bg.background_path) {
              this.imagePreview = this.constructImageUrl(bg.background_path);
              this.fileName = bg.name || 'Current Background';
            }
            this.existingId = bg.id || null;
          }
          console.log('Loaded background data:', {
            name: this.backgroundName,
            selectedRoomId: this.selectedRoomId,
            preview: this.imagePreview
          });
        }
      } catch (error) {
        console.error('Error fetching room background data:', error);
        // Handle error like user page
      } finally {
        this.isLoading = false;
      }
    },

    async getRooms() {
      try {
        this.loadingRooms = true;
        const response = await axios.get('/rooms');
        
        if (response.data && response.data.data) {
          this.rooms = response.data.data;
          console.log('Loaded rooms:', this.rooms);
        }
      } catch (error) {
        console.error('Error fetching rooms:', error);
        this.rooms = [];
      } finally {
        this.loadingRooms = false;
      }
    },

    onRoomChange(roomId) {
      console.log('Room selected:', roomId);
      // Additional logic can be added here if needed when room changes
    },
    triggerFileInput() {
      const fileInput = document.getElementById('background-file');
      if (fileInput) {
        fileInput.click();
      }
    },

    changeImage() {
      this.triggerFileInput();
    },

    onFileChange(event) {
      const file = event.target.files[0];
      if (!file) return;

      // Validate file type
      if (!file.type.startsWith("image/")) {
        alert(`${file.name} is not a valid image file`);
        return;
      }

      // Validate file size (max 10MB per image)
      if (file.size > 10 * 1024 * 1024) {
        alert(`${file.name} is too large. Maximum size is 10MB`);
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        this.selectedFile = file;
        this.imagePreview = e.target.result;
        this.fileName = file.name;
        this.fileSize = file.size;

        console.log("Image loaded:", {
          name: file.name,
          size: file.size,
          preview: "Preview available",
        });
      };

      reader.onerror = (error) => {
        console.error("Error reading file:", error);
        alert(`Error reading ${file.name}`);
      };

      reader.readAsDataURL(file);

      // Clear the input to allow re-selecting the same file
      event.target.value = "";
    },

    async save() {
      // Validate required fields
      if (!this.backgroundName || this.backgroundName.trim() === "") {
        alert("Please enter a background name");
        return;
      }

      try {
        this.isLoading = true;

        // Prepare FormData
        const formData = new FormData();
        formData.append("name", this.backgroundName.trim());
        formData.append("created_by_id", this.form.created_by_id || "");
        
        // Only append optional fields if they have values
        if (this.selectedRoomId) {
          formData.append("room_id", this.selectedRoomId);
        }

        // Only append file if a new image is selected
        if (this.selectedFile) {
          formData.append("background_path", this.selectedFile);
        }
        
        if (this.existingId) {
          formData.append("id", this.existingId);
        }

        console.log("Updating background:", {
          name: this.backgroundName,
          selectedRoomId: this.selectedRoomId,
          hasNewFile: !!this.selectedFile,
          existingId: this.existingId
        });

        // Send request to backend
        const response = await axios.post(
          `/room_backgrounds/${this.$route.params.backgroundId}`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
        
        // Save user timestamp
        await this.saveUserTimestamp(this.old_json, this.form);
        
        console.log("response", response);
        this.isLoading = false;
        this.$router.push('/room-backgrounds');
      } catch (error) {
        console.error('Update error:', error);
        if (error.response && error.response.data && error.response.data.message) {
          alert('Error: ' + error.response.data.message);
        } else {
          alert('An error occurred while updating the room background');
        }
        this.isLoading = false;
      }
    },

    async handleMultipleFileUpload(imagesToUpload) {
      const uploadPromises = [];

      imagesToUpload.forEach((slot, index) => {
        const formData = new FormData();
        formData.append("id", this.banners.id);
        formData.append("image_path", slot.file);
        formData.append("image_order", index);

        const uploadPromise = axios
          .post("upload_banners_image", formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .catch(function (error) {
            console.log(error);
            console.log("FAILURE!!");
          });

        uploadPromises.push(uploadPromise);
      });

      // Wait for all uploads to complete
      await Promise.all(uploadPromises);
    },

    viewImage(imageUrl) {
      this.selectedImageUrl = imageUrl;
      this.imageDialog = true;
    },

    formatFileSize(bytes) {
      if (bytes === 0) return "0 Bytes";

      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));

      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    },

    onImageError() {
      console.error("Image failed to load");
    },


    // Placeholder for saveUserTimestamp method
    async saveUserTimestamp(param1, param2) {
      // Add your timestamp saving logic here
      console.log("Saving user timestamp...", param1 + param2);
    },
  },
};
</script>

<style scoped>
.image-slot-col {
  padding: 8px;
}

.image-slot-card {
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  height: 300px;
}

.image-slot-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.upload-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  cursor: pointer;
  background-color: #fafafa;
  border: 2px dashed #e0e0e0;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.upload-area:hover {
  background-color: #f0f0f0;
  border-color: #2196f3;
}

.image-preview-container {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.preview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.image-preview-container:hover .preview-image {
  transform: scale(1.05);
}

.image-overlay {
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  gap: 8px;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.image-preview-container:hover .image-overlay {
  opacity: 1;
}

.action-btn {
  backdrop-filter: blur(10px);
  background-color: rgba(255, 255, 255, 0.1) !important;
}

.remove-slot-btn {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 2;
}

.image-info {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
  color: white;
  padding: 20px 12px 12px;
  font-size: 0.8rem;
}

.image-name {
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.image-size {
  opacity: 0.8;
  margin-top: 2px;
}

.action-buttons {
  display: flex;
  align-items: center;
  gap: 16px;
}

.upload-info {
  margin-top: 8px;
}

/* Animation for new slots */
.image-slot-col {
  animation: fadeInUp 0.5s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .image-slot-card {
    height: 250px;
  }
}

@media (max-width: 600px) {
  .action-buttons {
    flex-direction: column;
    align-items: stretch;
  }

  .action-buttons .v-btn {
    width: 100%;
    margin: 4px 0;
  }

  .image-slot-card {
    height: 200px;
  }
}
</style>
